import { useTranslation } from 'react-i18next'

import { Overline, Section, SectionTitle } from '@/design-system/primitives'

import { useEventContent, useEventFaqs } from '../api'
import { hasFixedPlace } from '../siteTexts'
import type { PublicEvent, PublicFaq } from '../types'
import { RubricPage } from '../components/RubricPage'
import { ContactSection, ContentSection, PracticalSection } from '../components/sections'

/** Le bandeau « masque obligatoire » appartient au hero de l'accueil. */
const MASK_POLICY_KEY = 'mask_policy'

/**
 * Infos pratiques et questions fréquentes.
 *
 * La FAQ occupait le bas de la page d'accueil, juste sous le bouton d'achat :
 * personne n'y arrivait, et elle repoussait le pied de page. Elle a sa place
 * ici, avec les blocs éditoriaux que l'accueil ne montre plus — c'est la page
 * qu'on ouvre quand on a une question, pas celle qu'on subit.
 */
export function InfoPage() {
  const { t } = useTranslation()

  return (
    <RubricPage
      title={t('rubrics.info.title')}
      intro={t('rubrics.info.body')}
      introKey="site.info_intro"
    >
      {(event) => <InfoBody slug={event.slug} event={event} />}
    </RubricPage>
  )
}

function InfoBody({ slug, event }: { slug: string; event: PublicEvent }) {
  const contentQuery = useEventContent(slug)
  const faqsQuery = useEventFaqs(slug)

  // L'accueil montre les deux premiers blocs ; ici, tous les autres. Les
  // textes à place fixe (`site.*`) ne sont pas des sections.
  const blocks = (contentQuery.data ?? []).filter(
    (block) => block.key !== MASK_POLICY_KEY && !hasFixedPlace(block.key),
  )

  return (
    <>
      <PracticalSection event={event} />
      {blocks.slice(2).map((block) => (
        <ContentSection key={block.key} block={block} />
      ))}
      <FaqSection faqs={faqsQuery.data ?? []} />
      <ContactSection event={event} />
    </>
  )
}

/**
 * Les questions fréquentes, en accordéon.
 *
 * Un `<details>` natif plutôt qu'un composant animé : il s'ouvre au doigt sur
 * n'importe quel téléphone, se lit sans JavaScript et se retrouve par la
 * recherche du navigateur. Sans question visible, la section n'existe pas —
 * un titre « FAQ » au-dessus du vide ferait croire à une page cassée.
 */
function FaqSection({ faqs }: { faqs: PublicFaq[] }) {
  const { t } = useTranslation()
  const shown = faqs.filter((faq) => faq.question.trim())
  if (shown.length === 0) return null

  return (
    <Section id="faq">
      <Overline>FAQ</Overline>
      <SectionTitle>{t('sections.faq')}</SectionTitle>
      <div className="mt-6 divide-y divide-line-subtle rounded-lg border border-line-subtle">
        {shown.map((faq) => (
          <details key={faq.id} className="group px-4 py-3 sm:px-5">
            <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-content-primary">
              <span>{faq.question}</span>
              <span
                aria-hidden
                className="mt-0.5 shrink-0 text-content-muted transition group-open:rotate-45"
              >
                +
              </span>
            </summary>
            {faq.answer ? (
              <p className="mt-3 whitespace-pre-line text-content-secondary">{faq.answer}</p>
            ) : null}
          </details>
        ))}
      </div>
    </Section>
  )
}
